/**
 * S2 Q3 — the relink confirmation sentence.
 *
 * Ruled 2026-08-12: allow the relink at any status, but confirm it with COUNTS, not adjectives.
 * Refusing above a status threshold was rejected because it refuses the exact case relink exists
 * for — a business genuinely changing hands mid-season is precisely a return that is already
 * filed — and there is no support path to refuse it into.
 *
 * "Some history will stay behind" does not make a staff member stop. "1 invoice" does.
 *
 * ⚠ THE TWO COUNTS ARE AT DIFFERENT GRAINS, AND THE SENTENCE SAYS SO. The invoice belongs to
 * THIS RETURN; outreach records are keyed to the CLIENT and therefore span that client's other
 * returns too. Ruled option (c): state both grains explicitly rather than merging them into one
 * total. A confidently wrong number is worse than an adjective, because the number is the thing
 * they will act on — a staff member reading "4 records" on one return will assume all four are
 * that return's, decide it is a big deal or a small one on that basis, and be wrong either way.
 *
 * Pure function so the wording is pinned by test rather than through the dialog.
 */

export interface RelinkConsequenceInput {
  /** Does THIS RETURN have an invoice? Return-scoped: 0 or 1. */
  hasInvoice: boolean;
  /** Outreach records keyed to the OLD CLIENT. Client-scoped; spans their other returns. */
  outreachCount: number;
  /** Display name of the client being relinked away from. */
  oldClientName: string;
  /** Null while the outreach count is still loading, so we never imply a measured zero. */
  outreachLoaded: boolean;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Returns the sentence shown before a relink is applied, or null when there is genuinely
 * nothing to leave behind — in which case a warning would be noise, and noise is what trains
 * people to click through the warnings that matter.
 */
export function buildRelinkConsequence(input: RelinkConsequenceInput): string | null {
  const { hasInvoice, outreachCount, oldClientName, outreachLoaded } = input;
  const who = oldClientName.trim() || 'the current client';

  // Not yet measured. Say that, rather than showing a zero we have not earned.
  if (!outreachLoaded) {
    return hasInvoice
      ? `This return's 1 invoice will stay with ${who}. Still checking for outreach records.`
      : `Still checking what will stay with ${who}.`;
  }

  if (!hasInvoice && outreachCount === 0) return null;

  const parts: string[] = [];
  if (hasInvoice) parts.push("this return's 1 invoice");
  if (outreachCount > 0) {
    parts.push(`${plural(outreachCount, 'outreach record', 'outreach records')} for ${who}`);
  }

  // "this return's 1 invoice, and 3 outreach records for X will stay where they are."
  const subject = parts.join(', and ');
  const verb = hasInvoice && outreachCount > 0 ? 'stay' : 'stays';

  return hasInvoice && outreachCount === 0
    ? `${capitalize(subject)} will stay with ${who}.`
    : `${capitalize(subject)} will ${verb} where ${parts.length > 1 ? 'they are' : 'it is'}.`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
